// Resolves the avatar/name/description/handle shown on the composer's
// preview header. Prefers the brand's connected social account (since
// that's how the post will appear once published).
//
// Fallback rule: when the social account is missing avatar/accountName,
// we only fill in from the logged-in bidlight user when *they* are the
// one who connected this account. Otherwise we'd leak the viewer's own
// photo/name onto a teammate's connected profile preview — that's how
// "Shagor's LinkedIn" ended up rendering with mouldi's photo whenever
// the SocialAccount row's avatar/accountName were empty.
//
// `description` prefers the social `heading` (e.g. LinkedIn headline:
// "CEO at Foo") over the longer `bio`, since most network previews show
// the headline under the name.
export function resolvePreviewAuthor(accountData, user) {
  const accountUid = accountData?.user_id ? String(accountData.user_id) : '';
  const loggedInUid = user?._id ? String(user._id) : '';
  // Treat "no owner info on the account" as "could be mine" so the
  // existing no-account fallback path (preview when nothing is
  // connected yet) keeps showing the logged-in user.
  const accountIsMine = !accountUid || !loggedInUid || accountUid === loggedInUid;

  const bidlightName = accountIsMine
    ? ([user?.name, user?.lastName].filter(Boolean).join(' ').trim() || user?.username)
    : '';
  const bidlightPhoto = accountIsMine ? user?.profilePhoto : '';

  return {
    name: accountData?.accountName || bidlightName || 'User Name',
    image: accountData?.avatar || bidlightPhoto || '/static/media/user.14128e69.png',
    description: accountData?.heading || accountData?.bio || '',
    handle: accountData?.handle || '',
  };
}

// Pick the social account whose profile drives the composer preview for
// a given platform. Order:
//   1. The logged-in user's own connected account on this platform (so
//      a team member previewing as themselves sees their own avatar +
//      handle, not the brand owner's).
//   2. Any other connected account on this brand for this platform —
//      better than the bidlight placeholder when a brand-owner connected
//      and a team-member is now drafting.
//   3. null — caller passes this through to resolvePreviewAuthor which
//      falls back to the bidlight user profile per-field.
//
// `socialAccounts` is the brand's array as returned by the brand fetch.
// `platform` is the currently-selected preview platform (lower-case).
// `userId` is the bidlight user's _id.
export function pickPreviewAccount({ socialAccounts, platform, userId }) {
  if (!Array.isArray(socialAccounts) || !platform) return null;
  const candidates = socialAccounts.filter(
    (a) => a && a.platform === platform && a.status === 'connected' && a.deleted !== true
  );
  if (!candidates.length) return null;
  const myUid = String(userId || '');
  const mine  = myUid && candidates.find((a) => String(a.user_id || '') === myUid);
  const pick  = mine || candidates[0];
  return {
    _id:         pick._id,
    platform:    pick.platform,
    status:      'active',
    user_id:     pick.user_id,
    accountName: pick.accountName || pick.title,
    bio:         pick.description,
    avatar:      pick.avatar,
    handle:      pick.socialHandle,
    heading:     pick.socialHeading,
    verified:    pick.verified,
    // Whether the picked account belongs to the logged-in user — useful
    // for the modal to show a hint like "previewing as yourself" vs
    // "no personal connection on this platform".
    isMine:      !!mine,
  };
}
